import { Box, Grid, Stack, styled, Typography } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { t } from "i18next";

import { useAppSelector } from "~/app/store";
import useFlag from "~/config/flags";
import { ActivityState } from "~/features/autostoreBin";
import { AutostoreBinWithAnimationFlagged } from "~/features/autostoreBin/AutostoreBinWithAnimationFlagged";
import { useClientConfig } from "~/hooks/useClientConfig";
import { usePortStatusQuery } from "~/hooks/usePortStatus";
import {
  selectBinIsPresent,
  selectBinIsPresentBinId,
  selectLeftOrRightPortActive,
  selectPickingState,
  selectShowTwoBinComponents
} from "~/redux/selectors/autostoreSelectors";
import { selectPickQuantityForDisplay } from "~/redux/selectors/outOfStockSelectors";
import { selectStartPickingModalIsOpen } from "~/redux/selectors/pickingSelectors";
import { selectWorkstationAvailablePorts } from "~/redux/selectors/workstationsSelectors";
import { useGetInventoryByIdQuery } from "~/redux/warehouse/inventory.hooks";
import { PickDto } from "~/types/api";

import { selectPickCompartment } from "./autostorePicking.slice";

const TwoBinContainer = styled(Grid)`
  display: flex;
  width: 150%;
  position: relative;
  left: -25%;
  margin-bottom: 30px;
`;

type Props = {
  currentPick: PickDto | null;
};

export default function PickingBins({ currentPick }: Props) {
  const { isBinAnimationEnabled } = useFlag();

  const binIsPresent = useAppSelector(selectBinIsPresent);
  const binIsPresentBinId = useAppSelector(selectBinIsPresentBinId);
  const showTwoBinComponents = useAppSelector(selectShowTwoBinComponents);
  const pickingState = useAppSelector(selectPickingState);
  const batchCompletedModalOpen = useAppSelector(selectStartPickingModalIsOpen);
  const leftOrRightPortActive = useAppSelector(selectLeftOrRightPortActive);
  const pickQuantityForDisplay = useAppSelector(selectPickQuantityForDisplay);
  const pickCompartment = useAppSelector(selectPickCompartment);
  const [portId] = useAppSelector(selectWorkstationAvailablePorts);
  const { isReady } = usePortStatusQuery(portId);

  const { ap_fusionPortScreenEnabled, ap_showPickQtyWhileBinLoaderIsActive } =
    useClientConfig();
  const displayTwoBinComponents =
    ap_fusionPortScreenEnabled && showTwoBinComponents;

  const { data: inventory } = useGetInventoryByIdQuery(
    currentPick ? { inventoryId: currentPick.inventoryId } : skipToken,
    { refetchOnMountOrArgChange: true }
  );
  const isInventoryOnHold = !!inventory?.holds?.length;
  const isBinFlagged = inventory?.bin?.autostoreBin?.isFlagged;

  const numberOfColumns =
    pickingState?.binConfiguration?.verticalCompartmentCount;
  const numberOfRows =
    pickingState?.binConfiguration?.horizontalCompartmentCount;

  const binNumber =
    currentPick?.pickBin?.autostoreBin?.autostoreBinId || binIsPresentBinId;

  const getBinIdForDisplay = () =>
    isBinFlagged
      ? ` ${t("bin is flagged", { binNumber })}`
      : `${t("bin")} ${binNumber}`;

  const getBinState = (isActive: boolean): ActivityState | undefined => {
    if (batchCompletedModalOpen) {
      return "Port Closed";
    }
    if (isBinAnimationEnabled) {
      if (displayTwoBinComponents && isReady && !isActive) {
        return "Inactive";
      }
      return undefined;
    } else {
      if (!binIsPresent) {
        return "Waiting for Bin";
      } else if (isActive) {
        return "Bin Opened";
      } else {
        return "Inactive";
      }
    }
  };

  return (
    <>
      {displayTwoBinComponents ? (
        <TwoBinContainer>
          {(["Left", "Right"] as const).map((side) => {
            const isActive = side === leftOrRightPortActive;
            const showBinId =
              !!binNumber && (!isReady || (isReady && isActive));
            return (
              <Stack
                key={side}
                alignItems="center"
                flexGrow={1}
                // Todo: refactor the parent elements' styling to remove this (WMP-2165).
                // Without this maxWidth, the bins shift position between states
                maxWidth="49%"
              >
                <AutostoreBinWithAnimationFlagged
                  portId={portId}
                  state={getBinState(isActive)}
                  numberOfColumns={(isActive && numberOfColumns) || 1}
                  numberOfRows={(isActive && numberOfRows) || 1}
                  isBinFlagged={isActive ? isBinFlagged : undefined}
                  isSelectedCompartmentInventoryOnHold={
                    isActive ? isInventoryOnHold : undefined
                  }
                  pickCompartment={pickCompartment}
                  pickQuantity={pickQuantityForDisplay}
                  binId={
                    currentPick?.pickBin?.autostoreBin?.autostoreBinId ||
                    binIsPresentBinId
                  }
                  showPickQtyWhileBinLoaderIsActive={
                    ap_showPickQtyWhileBinLoaderIsActive
                  }
                  hideBinId
                />

                {showBinId && (
                  <Box style={{ height: 25 }}>
                    <Typography variant="h5" textAlign="center">
                      {getBinIdForDisplay()}
                    </Typography>
                  </Box>
                )}
              </Stack>
            );
          })}
        </TwoBinContainer>
      ) : (
        <Stack alignItems="center">
          <AutostoreBinWithAnimationFlagged
            portId={portId}
            state={getBinState(true)}
            numberOfColumns={numberOfColumns || 1}
            numberOfRows={numberOfRows || 1}
            pickCompartment={pickCompartment}
            pickQuantity={pickQuantityForDisplay}
            binId={currentPick?.pickBin?.autostoreBin?.autostoreBinId}
            showPickQtyWhileBinLoaderIsActive={
              ap_showPickQtyWhileBinLoaderIsActive
            }
            isBinFlagged={isBinFlagged}
            isSelectedCompartmentInventoryOnHold={isInventoryOnHold}
            hideBinId
          />
        </Stack>
      )}
      <Box style={{ height: 25 }}>
        {!!binNumber && !ap_fusionPortScreenEnabled && binIsPresent && (
          <Typography variant="h5" textAlign="center">
            {getBinIdForDisplay()}
          </Typography>
        )}
      </Box>
    </>
  );
}
